/**
 * A slot in the AppShell header that a page can fill.
 *
 * Why this exists: the Fleet Editor's facet tabs (Persona / Skills / Workflows
 * / …) used a row of their own directly under the header, costing ~50px on
 * every facet. The breadcrumb beside them — "Matthaios Mission Control / Fleet
 * Editor" — already sits on a row with room to share. Putting the tabs there
 * reclaims the whole row without removing any navigation.
 *
 * That room is finite, which is what the first version of this comment missed:
 * below ~1100px the row runs out and slot content painted over the account
 * chip. The shell now clips the row and gives the slot a floored share of the
 * leftover space, so what fits inside that share is the page's problem rather
 * than the slot's. A page whose nav can lose items fills the slot with
 * `OverflowNav`, which measures its own box and moves whatever no longer fits
 * into a **More** disclosure; the slot itself stays a plain ReactNode and
 * measures nothing.
 *
 * A context rather than a DOM portal because the tabs are ordinary React —
 * Links that must re-render when the agent or active facet changes — and a
 * portal would leave them rendering into a node the shell may not have mounted
 * yet on first paint.
 *
 * A page that never sets anything leaves the slot empty and the header exactly
 * as it was, so this is inert for every surface but the Fleet Editor.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

interface HeaderSlotValue {
  content: ReactNode
  setContent: (node: ReactNode) => void
}

const HeaderSlotContext = createContext<HeaderSlotValue>({
  content: null,
  // Default no-op: a page rendered outside the shell (a test mounting a facet
  // directly) must not crash for wanting to decorate a header that isn't there.
  setContent: () => {},
})

export function HeaderSlotProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ReactNode>(null)
  const value = useMemo(() => ({ content, setContent }), [content])
  return <HeaderSlotContext.Provider value={value}>{children}</HeaderSlotContext.Provider>
}

/** Read the slot's current content — for the shell's header to render. */
export function useHeaderSlotContent(): ReactNode {
  return useContext(HeaderSlotContext).content
}

/**
 * Fill the header slot for as long as this component is mounted, clearing it on
 * unmount so a page cannot leave its chrome behind on the next one.
 *
 * `deps` is the caller's honest statement of what the node depends on, the same
 * contract as useEffect/useMemo. It exists because a ReactNode is a fresh object
 * every render: keying the effect on the node itself would set state on every
 * render and loop.
 */
export function useHeaderSlot(node: ReactNode, deps: unknown[]) {
  const { setContent } = useContext(HeaderSlotContext)
  useEffect(() => {
    setContent(node)
    return () => setContent(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}
